import React from "react";
import { Plus, Search, Trash2 } from "lucide-react";
import LoadingSpinner from "../../components/ui/LoadingSpinner";
import ErrorMessage from "../../components/ui/ErrorMessage";
import { Input, Textarea } from "../../components/ui/Input";
import { conditionRulesApi } from "../../services/api";
import type {
  ConditionRule,
  ConditionRuleCreate,
} from "../../types/node_group";
import { toast } from "react-hot-toast";
import { Link, useNavigate } from "react-router-dom";

export default function ConditionRulesPage() {
  const [items, setItems] = React.useState<ConditionRule[]>([]);
  const [loading, setLoading] = React.useState<boolean>(true);
  const [error, setError] = React.useState<string | null>(null);
  const [search, setSearch] = React.useState<string>("");
  const [page, setPage] = React.useState<number>(1);
  const [pageSize, setPageSize] = React.useState<number>(20);
  const [creating, setCreating] = React.useState<boolean>(false);
  const [form, setForm] = React.useState<{
    name: string;
    description?: string | null;
    type: string;
  }>({ name: "", description: "", type: "any_input" });
  const navigate = useNavigate();

  const fetchList = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await conditionRulesApi.list({
        search: search || undefined,
        global_only: true,
        page,
        page_size: pageSize,
      });
      setItems(res.rules || []);
    } catch (e: any) {
      const msg =
        e?.response?.data?.detail ||
        e?.message ||
        "Failed to load condition rules";
      setError(String(msg));
    } finally {
      setLoading(false);
    }
  }, [search, page, pageSize]);

  React.useEffect(() => {
    fetchList();
  }, [fetchList]);

  const onCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (!form.name.trim()) {
        toast.error("Name is required");
        return;
      }
      const payload: ConditionRuleCreate = {
        name: form.name.trim(),
        description: form.description || null,
        type: form.type || "any_input",
      } as any;
      const created = await conditionRulesApi.create(payload);
      toast.success("Condition rule created");
      setCreating(false);
      setForm({ name: "", description: "", type: "any_input" });
      // Prepend to list or refetch
      setItems((prev) => [created, ...prev]);
    } catch (e: any) {
      const msg =
        e?.response?.data?.detail ||
        e?.message ||
        "Failed to create condition rule";
      toast.error(String(msg));
    }
  };

  const onDelete = async (id: number) => {
    if (!confirm("Delete this condition rule?")) return;
    try {
      await conditionRulesApi.remove(id);
      toast.success("Condition rule deleted");
      setItems((prev) => prev.filter((r) => r.id !== id));
    } catch (e: any) {
      const msg =
        e?.response?.data?.detail ||
        e?.message ||
        "Failed to delete condition rule";
      toast.error(String(msg));
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Condition Rules</h1>
        <button
          className="inline-flex items-center px-3 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700"
          onClick={() => setCreating(true)}
        >
          <Plus className="w-4 h-4 mr-2" /> New Rule
        </button>
      </div>

      <div className="flex items-center space-x-2">
        <div className="relative">
          <Search className="w-4 h-4 absolute left-2 top-1/2 -translate-y-1/2 text-gray-400" />
          <Input
            type="text"
            placeholder="Search by name..."
            className="pl-8 pr-3 py-2 w-72"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            variant="rounded-md"
            showLabel={false}
          />
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center items-center py-12">
          <LoadingSpinner />
        </div>
      ) : error ? (
        <ErrorMessage message={error} onRetry={fetchList} className="py-12" />
      ) : (
        <div className="bg-white shadow rounded">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Description
                </th>
                <th className="px-6 py-3" />
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {items.map((rule) => (
                <tr
                  key={rule.id}
                  className="cursor-pointer hover:bg-gray-50"
                  onClick={() => navigate(`/condition-rules/${rule.id}/edit`)}
                >
                  <td className="px-6 py-4 text-sm text-indigo-700">
                    <span className="hover:underline">{rule.name}</span>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-700">
                    {rule.type}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500">
                    {rule.description || "-"}
                  </td>
                  <td className="px-6 py-4 text-right space-x-2">
                    <button
                      className="inline-flex items-center px-2 py-1 text-red-600 hover:text-red-700"
                      onClick={(e) => {
                        e.stopPropagation();
                        onDelete(rule.id);
                      }}
                    >
                      <Trash2 className="w-4 h-4 mr-1" /> Delete
                    </button>
                  </td>
                </tr>
              ))}
              {items.length === 0 && (
                <tr>
                  <td
                    colSpan={4}
                    className="px-6 py-10 text-center text-sm text-gray-500"
                  >
                    No condition rules found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {creating && (
        <div className="fixed inset-0 z-50">
          <div
            className="absolute inset-0 bg-black/40"
            onClick={() => setCreating(false)}
          />
          <div className="absolute right-0 top-0 h-full w-full max-w-md bg-white shadow-xl flex flex-col">
            <div className="p-4 border-b">
              <h2 className="text-lg font-semibold">Create Condition Rule</h2>
            </div>
            <form onSubmit={onCreate} className="p-4 space-y-4 overflow-y-auto">
              <div>
                <Input
                  label="Name"
                  type="text"
                  value={form.name}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, name: e.target.value }))
                  }
                  variant="rounded-md"
                  required
                />
              </div>
              <div>
                <Input
                  label="Type"
                  type="text"
                  value={form.type}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, type: e.target.value }))
                  }
                  variant="rounded-md"
                />
              </div>
              <div>
                <Textarea
                  label="Description"
                  value={form.description || ""}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      description: e.target.value,
                    }))
                  }
                  variant="rounded-md"
                />
              </div>
              <div className="flex justify-end space-x-2">
                <button
                  type="button"
                  className="px-3 py-2 rounded border"
                  onClick={() => setCreating(false)}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-3 py-2 rounded bg-indigo-600 text-white"
                >
                  Create
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
